<!-- The main application drawer/sidebar-menu. -->

<template>
  <v-navigation-drawer
    v-show="$store.getAuthToken() && $user"
    :value="show"
    app
    fixed
    clipped
    :width="$vuetify.breakpoint.width <= 340 ? 260 : 300"
    :disable-resize-watcher="disableResizeWatcher"
    :disable-route-watcher="disableRouteWatcher"
    :touchless="$store.state.common.runtimeEnv.iOS"
    class="pb-0"
    :class="$desktopModeStyles"
    @input="(val) => $emit('update:show', val)"
  >
    <v-layout
      column
      fill-height
    >
      <v-list :dense="$vuetify.breakpoint.xsOnly">
        <!-- View Items -->
        <template v-for="(item, i) in activeViewItems">
          <v-list-tile
            :key="'viewItem-' + i"
            :to="item.route"
            :title="$t(item.title)"
            :data-cy="item.data_cy"
            @click="item.action ? item.action() : null"
          >
            <v-list-tile-action>
              <!-- Dashboard item uses a special image -->
              <img
                v-if="item.title === 'dashboard'"
                width="30"
                height="22"
                :src="
                  $route.path === '/dashboard'
                    ? '/static/logo_my_dashboard_selected.svg'
                    : '/static/logo_my_dashboard.svg'
                "
              >
              <!-- All other items just show their icon -->
              <v-icon v-else>
                {{ item.icon }}
              </v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title style="overflow: initial">
                <!-- Unseen Contents -->
                <v-badge
                  v-if="item.title === 'contents'"
                  color="blue"
                  :class="{
                    smaller: $store.state.common.numUnseen.contents > 99,
                    'nudged-down': $vuetify.breakpoint.xsOnly,
                  }"
                >
                  <span
                    v-if="$store.state.common.numUnseen.contents"
                    slot="badge"
                  >
                    {{
                      $store.state.common.numUnseen.contents > 99
                        ? '99+'
                        : $store.state.common.numUnseen.contents
                    }}
                  </span>
                  {{ $t(item.title) }}
                </v-badge>
                <!-- Unseen Downloads -->
                <v-badge
                  v-else-if="item.title === 'downloads'"
                  color="blue"
                  :class="{
                    smaller: $store.state.common.numUnseen.downloads > 99,
                    'nudged-down': $vuetify.breakpoint.xsOnly,
                  }"
                >
                  <span
                    v-if="$store.state.common.numUnseen.downloads"
                    slot="badge"
                  >
                    {{
                      $store.state.common.numUnseen.downloads > 99
                        ? '99+'
                        : $store.state.common.numUnseen.downloads
                    }}
                  </span>
                  {{ $t(item.title) }}
                </v-badge>
                <!-- New/highlighted Channels -->
                <v-badge
                  v-else-if="item.title === 'music'"
                  color="blue"
                  :class="{
                    'nudged-down': $vuetify.breakpoint.xsOnly,
                  }"
                >
                  <span
                    v-if="$store.getters.promotedRadioChannels.length > 0"
                    slot="badge"
                  >
                    {{ $store.getters.promotedRadioChannels.length }}
                  </span>
                  {{ $t(item.title) }}
                </v-badge>
                <!-- All other items just show their title -->
                <span v-else>{{ $t(item.title) }}</span>
              </v-list-tile-title>
            </v-list-tile-content>
            <v-hover v-if="item.extra && item.extra.conditional()">
              <v-list-tile-action
                slot-scope="{hover}"
                :title="$t('uploadContent')"
                @click.prevent.stop="item.extra.action"
              >
                <v-icon :class="{'msr--text': hover}">
                  {{ item.extra.icon }}
                </v-icon>
              </v-list-tile-action>
            </v-hover>
          </v-list-tile>
        </template>
        <v-divider />
        <!-- Addon Items -->
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ $t('addons') }}
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <!-- Addon Items: Content Creator -->
        <v-list-tile
          v-if="
            $store.getters.userManagesContentForAtLeastOneGroup &&
              $store.getters.showContentCreatorLandingPageItem
          "
          :to="{name: 'contentcreatorlanding'}"
        >
          <v-list-tile-action>
            <v-icon>auto_fix_high</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title style="overflow: initial">
              {{ $t('contentcreatorlanding') }}
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-group
          v-if="
            $store.getters.userManagesContentForAtLeastOneGroup &&
              $store.getters.showAllContentCreatorItems
          "
          v-model="contentCreatorDrawerItemsExpanded"
          data-cy="contentcreator-list-group"
          class="v-list-group--no-top-border"
        >
          <v-list-tile slot="activator">
            <v-list-tile-action>
              <v-icon>auto_fix_high</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title style="overflow: initial">
                {{ $t('contentcreator') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <!-- TVTemplates -->
          <v-list-tile
            :to="{name: 'tvtemplates'}"
            @click="$store.commit('RESET_TV_TEMPLATES_FILTER')"
          >
            <v-list-tile-action class="ml-3">
              <v-icon>web</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('tvtemplates') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <!-- Branding -->
          <v-list-tile
            :disabled="!$store.getters.userCanUseBrandings"
            :to="{name: 'branding'}"
            data-cy="brandings-tile"
          >
            <v-list-tile-action class="ml-3">
              <v-icon>format_paint</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('branding') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list-group>
        <!-- Addon Items: Other -->
        <transition-group name="addons-list-transition">
          <v-list-tile
            v-for="(item, i) in activeAddonItems"
            :key="'addonItem-' + i"
            :to="item.route"
            :title="$t(item.title)"
            :data-cy="item.data_cy"
            :disabled="item.disabled ? item.disabled() : false"
            @click="item.action ? item.action() : null"
          >
            <v-list-tile-action>
              <v-icon :disabled="item.disabled ? item.disabled() : false">
                {{ item.icon }}
              </v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title style="overflow: initial">
                <!-- Marketing Manager Badge -->
                <v-badge
                  v-if="item.title == 'marketingmanager'"
                  color="blue"
                  :class="{
                    smaller: $store.state.common.numUnseen.docmgr > 99,
                    'nudged-down': $vuetify.breakpoint.xsOnly,
                  }"
                >
                  <span
                    v-if="$store.state.common.numUnseen.docmgr"
                    slot="badge"
                  >
                    {{
                      $store.state.common.numUnseen.docmgr > 99
                        ? '99+'
                        : $store.state.common.numUnseen.docmgr
                    }}
                  </span>
                  {{ $t(item.title) }}
                </v-badge>
                <v-badge
                  v-else
                  color="transparent msr--text"
                  class="smaller"
                >
                  <!-- Visitorcount Demo Badge -->
                  <span
                    v-if="
                      item.title == 'visitorcount' &&
                        $userprofile &&
                        $userprofile.visitorcount_demo_enabled &&
                        $store.state.common.showBetaFeatureMarkers
                    "
                    slot="badge"
                    class="highlighted-badge-text"
                  >
                    Demo
                  </span>
                  <!-- Menus Demo Badge -->
                  <span
                    v-else-if="
                      item.title == 'menus' &&
                        $userprofile &&
                        (menus.length == 0 ||
                          menus.filter((menu) => menu.is_demo).length > 0) &&
                        $store.state.common.showBetaFeatureMarkers
                    "
                    slot="badge"
                    class="highlighted-badge-text"
                  >
                    Demo
                  </span>
                  <span>{{ $t(item.title) }}</span>
                </v-badge>
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </transition-group>
        <v-divider v-if="showManagementItems && !managementItemsExpanded" />
        <!-- Management Items -->
        <v-list-group
          v-if="showManagementItems"
          v-model="managementItemsExpanded"
          data-cy="drawer-toggle-management-items"
        >
          <template slot="activator">
            <v-list-tile>
              <v-list-tile-action>
                <v-icon>settings</v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ $t('administration') }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </template>
          <template v-for="(item, i) in activeManagementItems">
            <v-list-tile
              :key="'managementItem-' + i"
              :to="item.route"
              :title="$t(item.title)"
              :data-cy="item.data_cy"
              class="ml-3"
              @click="item.action ? item.action() : null"
            >
              <v-list-tile-action>
                <v-badge
                  color="transparent msr--text"
                  class="smaller"
                >
                  <span
                    v-if="
                      $userprofile &&
                        $userprofile.is_being_impersonated &&
                        item.title == 'account'
                    "
                    slot="badge"
                    :title="$t('isBeingImpersonated')"
                    class="highlighted-badge-text"
                  >MY</span>
                  <user-avatar-image
                    v-if="item.title === 'account' && hasAvatarImage"
                    :user="$user"
                    :size="24"
                  />
                  <v-icon v-else>
                    {{ item.icon }}
                  </v-icon>
                </v-badge>
              </v-list-tile-action>
              <v-list-tile-content class="auto-height">
                <v-list-tile-title style="overflow: initial">
                  <span
                    v-if="$user && item.title === 'account'"
                    data-hj-suppress
                    style="
                      max-width: 100%;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                  >
                    {{ userLabel }}
                  </span>
                  <span v-else>
                    {{ $t(item.title) }}
                  </span>
                </v-list-tile-title>
                <v-list-tile-sub-title
                  v-if="item.title === 'workspace'"
                  class="mb-1"
                >
                  {{ $userprofile.workspace.name }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </v-list-group>
        <!-- Feedback -->
        <v-list-tile
          :title="$t('feedback')"
          @click="goToFeedbackPage()"
        >
          <v-list-tile-action>
            <v-icon :class="{'msr--text': $route.name === 'feedback'}">
              speaker_notes
            </v-icon>
          </v-list-tile-action>
          <v-list-tile-title :class="{'msr--text': $route.name === 'feedback'}">
            {{ $t('feedback') }}
          </v-list-tile-title>
        </v-list-tile>
        <!-- About -->
        <v-list-tile
          :title="$t('contact')"
          @click="goToAboutPage()"
        >
          <v-list-tile-action>
            <v-icon :class="{'msr--text': $route.path === '/about'}">
              info_outline
            </v-icon>
          </v-list-tile-action>
          <v-list-tile-title :class="{'msr--text': $route.path === '/about'}">
            {{ $t('contact') }}
          </v-list-tile-title>
        </v-list-tile>
        <!-- Logout -->
        <v-list-tile
          :title="$t('logout')"
          data-cy="drawer-logout-tile"
          @click="logout"
        >
          <v-list-tile-action>
            <v-icon style="transform: rotate(180deg)">
              exit_to_app
            </v-icon>
          </v-list-tile-action>
          <v-list-tile-title>Logout</v-list-tile-title>
        </v-list-tile>
      </v-list>
      <v-spacer />
    </v-layout>
  </v-navigation-drawer>
</template>

<script>
import {redirectToExternalUrl} from '@/utils';

import UserAvatarImage from '@/components/UserAvatarImage';

export default {
  components: {
    UserAvatarImage,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    userLabel: {
      type: String,
      required: true,
    },
  },
  data() {
    const router = this.$router;
    const store = this.$store;

    return {
      loadingMenus: true,
      viewItems: [
        {
          title: 'dashboard',
          route: '/dashboard',
        },
        {
          icon: 'library_music',
          title: 'contents',
          route: '/content',
          data_cy: 'contents-tile',
          action: () => {
            store.commit('RESET_LAST_CONTENTS_QUERY');
            store.commit('RESET_CONTENT_FILTERS');
          },
          extra: {
            icon: 'file_upload',
            conditional() {
              return store.getters.userManagesContentForAtLeastOneGroup;
            },
            action: () => {
              if (this.$route.name === 'upload') {
                this.$bus.$emit('reset-content-upload-page');
                return;
              }
              router.push({name: 'upload'});
            },
          },
        },
        {
          icon: 'location_on',
          title: 'sites',
          route: '/sites',
          action: () => {
            store.commit('RESET_SITES_FILTERS');
            store.commit('RESET_SITES');
          },
        },
        {
          icon: 'volume_up',
          title: 'music',
          route: '/music',
          conditional: () => {
            if (!this.$user) {
              return false;
            }
            if (!this.$userprofile.workspace) {
              return true;
            }
            return !this.$userprofile.workspace.radiochannels_disabled;
          },
        },
        {
          icon: 'flight_takeoff',
          title: 'onboarding',
          data_cy: 'onboarding-tile',
          conditional: () => {
            if (!this.$user) {
              return false;
            }
            return this.$userprofile.onboarding;
          },
          action: () => {
            redirectToExternalUrl(this, this.$userprofile.onboarding.url);
          },
        },
        {
          icon: 'flag',
          title: 'campaigns',
          route: '/campaigns',
          conditional: () => {
            if (!this.$user) {
              return false;
            }
            return (
              this.$userprofile.has_campaigns ||
              (this.$userprofile.workspace &&
                this.$userprofile.workspace.campaign_creation_enabled)
            );
          },
          action: () => {
            // If we already have fetched a list of Campaigns (triggered
            // by the page component), make subsequent clicks on the
            // drawer option trigger a refetch.
            if (this.$store.state.common.campaignsPage.results.length > 0) {
              this.$store.dispatch('LIST_CAMPAIGNS');
            }
          },
        },
        {
          icon: 'file_download',
          title: 'downloads',
          route: '/downloads',
          conditional: () => {
            if (!this.$user) {
              return false;
            }
            return this.$store.getters.userHasDownloads;
          },
        },
      ],
      addonItems: [
        {
          icon: 'timeline',
          title: 'visitorcount',
          action: () => {
            redirectToExternalUrl(this, '/client/visitorcount');
          },
        },
        {
          icon: 'description',
          title: 'marketingmanager',
          disabled: () => {
            if (!this.$user) {
              return true;
            }
            return !this.$userprofile.docmgr_url;
          },
          action: () => {
            redirectToExternalUrl(this, this.$userprofile.docmgr_url);
          },
        },
        {
          icon: 'restaurant',
          title: 'menus',
          route: '/menus',
          data_cy: 'menus-tile',
          conditional: () => {
            return (
              !this.loadingMenus &&
              !!this.$userprofile &&
              !!this.$userprofile.workspace &&
              (this.$userprofile.workspace.has_menus ||
                // FEATUREFLAG-2636
                this.$user.email.includes('@myshopradio'))
            );
          },
        },
      ],
      managementItemsExpanded: false,
      managementItems: [
        {
          icon: 'person',
          title: 'account',
          route: '/account',
          conditional: () => {
            return !!this.$userprofile && this.$vuetify.breakpoint.xs;
          },
        },
        {
          icon: 'supervisor_account',
          title: 'team',
          route: '/users/manage',
        },
        {
          icon: 'rss_feed',
          title: 'brandSelection',
          route: '/brands',
          conditional: () => {
            if (!this.$user) {
              return false;
            }
            return this.$userprofile.available_brand_groups.length > 0;
          },
        },
        {
          icon: 'bar_chart',
          title: 'statistics',
          route: '/statistics',
          conditional: () => {
            return this.$store.getters.userCanSeeContentStoreStatistics;
          },
        },
        {
          icon: 'storage',
          title: 'devices',
          route: '/devices',
          conditional: () => {
            return this.$store.getters.userCanWriteToAtLeastOneStore;
          },
        },
        {
          icon: 'tag',
          title: 'tags',
          route: '/tags/manage',
          conditional: () => {
            return (
              this.$store.getters.userManagesContentForAtLeastOneGroup &&
              this.$userprofile &&
              this.$userprofile.can_use_tagging
            );
          },
        },
        {
          icon: 'folder',
          title: 'categories',
          route: '/categories',
          action: () => {
            store.dispatch('LIST_CATEGORIES');
          },
          conditional: () => {
            return this.$store.getters.userManagesContentForAtLeastOneGroup;
          },
        },
        {
          icon: 'timelapse',
          title: 'timeframe',
          route: '/timeframes',
          conditional: () => {
            return this.$store.getters.userCanSetTimeFrames;
          },
        },
        {
          icon: 'schedule',
          title: 'openinghours',
          route: '/openinghours',
          conditional: () => {
            return this.$store.getters.userCanWriteToAtLeastOneStore;
          },
        },
        {
          icon: 'home_work',
          title: 'workspace',
          route: '/workspace',
          conditional: () => {
            return (
              this.$userprofile &&
              !!this.$userprofile.workspace &&
              !this.$userprofile.is_association_workspace_user
            );
          },
        },
      ],
    };
  },
  computed: {
    menus() {
      return this.$store.state.common.menus;
    },
    contentCreatorDrawerItemsExpanded: {
      get() {
        return this.$store.state.common.contentCreatorDrawerItemsExpanded;
      },
      set(expanded) {
        this.$store.commit(
          'SET_CONTENTCREATOR_DRAWER_ITEMS_EXPANDED',
          expanded
        );
      },
    },
    hasAvatarImage() {
      return this.$userprofile && this.$userprofile.avatar_image_url;
    },
    campaigns() {
      return this.$store.state.common.campaigns;
    },
    /**
     * This functionality should be disabled until we are logged in.
     */
    disableResizeWatcher() {
      return !this.$user;
    },
    /**
		 * We don't want to open the drawer on route changes, since that
		 * would make the minimize-feature irrelevant, however this also
		 * turns off that the drawer closes itself on route changes on
		 * non-desktop devices and this we actually want. Make it toggle
		   depending on device size therefore.
		 */
    disableRouteWatcher() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    activeViewItems() {
      return this.viewItems.filter(
        (item) => !item.conditional || item.conditional()
      );
    },
    activeManagementItems() {
      return this.managementItems.filter(
        (item) => !item.conditional || item.conditional()
      );
    },
    activeAddonItems() {
      return this.addonItems.filter(
        (item) => !item.conditional || item.conditional()
      );
    },
    showManagementItems() {
      // Since this now contains things like our own profile page, always show it.
      return true;
    },
  },
  watch: {
    show: function (doShow) {
      if (doShow) {
        this.fetchUnseenData();
      }
    },
  },
  created() {
    if (this.$user) {
      this.fetchMenusIfNotDoneYet();
    } else {
      this.$bus.$on('user-available', () => {
        this.fetchMenusIfNotDoneYet();
      });
    }
  },
  mounted() {
    this.setInitialDrawerState();

    if (!this.$store.getAuthToken()) {
      return;
    }

    this.maybeFetchRadioChannels();

    this.$intervals.registerLongpollingCall(
      this.fetchUnseenData,
      this.$intervals.every15Minutes
    );
  },
  methods: {
    fetchMenusIfNotDoneYet() {
      if (this.$store.state.common.menusLastFetchedAt != null) {
        return;
      }

      this.loadingMenus = true;
      this.$store.dispatch('LIST_MENUS').finally(() => {
        this.loadingMenus = false;
      });
    },
    /**
     * Fetch channels iniitally so we can display the badge for featured ones.
     */
    maybeFetchRadioChannels() {
      const channelsNotFetchedYet =
        this.$store.state.common.radioChannels.length === 0;
      if (channelsNotFetchedYet) {
        this.$store.dispatch('LIST_RADIO_CHANNELS');
      }
    },
    setInitialDrawerState() {
      const showDrawer =
        !!this.$store.getAuthToken() && this.$vuetify.breakpoint.lgAndUp;
      this.$emit('update:show', showDrawer);
    },
    /**
     * Logout and redirect to login view.
     */
    logout() {
      this.$emit('update:show', false);

      this.$nextTick(() => {
        this.$api.logout().then(() => {});
      });
    },
    fetchUnseenData() {
      this.$store.dispatch('RETRIEVE_NUM_UNSEEN');
    },
    goToFeedbackPage() {
      this.$router.push('/feedback');
    },
    goToAboutPage() {
      this.$router.push('/about');
    },
  },
};
</script>

<style scoped>
.update-needed-tile {
  position: relative;
  top: -8px;
}

.update-needed-tile >>> .v-list__tile {
  height: auto;
}
.update-needed-tile >>> .v-list__tile .v-list__tile__title {
  height: auto;
  white-space: normal;
}

.v-list-group--no-top-border::before {
  background: none !important;
}

.addons-list-transition-enter-active,
.addons-list-transition-leave-active {
  transition: all 0.25s ease-in-out;
}
.addons-list-transition-enter,
.addons-list-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/components/AppDrawer.vue